import type { ComponentProps, FocusEventHandler, ReactNode, Ref } from 'react';

import { useFieldWiring } from '../hooks/use-field-wiring';
import { cn } from '../lib/utils';
import { Field, FieldLabel, type FieldOverrides, FieldSlot } from './field';
import { inputInvalidClassName } from './input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from './select';

// Radix throws on a `SelectItem` with `value=""` because `''` is its signal to show the placeholder,
// so the clear option needs a stand-in value that `onValueChange` maps back to `''`.
const CLEAR_VALUE = '__select-field-clear__';
const CLEAR_LABEL = 'None';

type Option = { value: string; label: string };

type Props = Omit<ComponentProps<typeof Select>, 'children' | 'onValueChange'> & {
    label: string;
    description?: string;
    error?: boolean | ReactNode;
    fieldProps?: FieldOverrides;
    fieldClassName?: string;
    id?: string;
    options: Option[];
    placeholder?: string;
    clearable?: boolean;
    onChange?: (value: string) => void;
    onBlur?: FocusEventHandler<HTMLButtonElement>;
    ref?: Ref<HTMLButtonElement>;
};

function SelectField({
    label,
    description,
    error,
    id,
    fieldProps,
    fieldClassName,
    options,
    placeholder,
    clearable = false,
    onChange,
    onBlur,
    ref,
    ...selectProps
}: Props) {
    const wiring = useFieldWiring({ id, description, error });
    const { className: fieldPropsClassName, ...restFieldProps } = fieldProps ?? {};

    if (options.some((option) => option.value === '')) {
        throw new Error(
            'SelectField: an option cannot have an empty value, Radix reserves "" for the placeholder.',
        );
    }

    if (clearable && options.some((option) => option.value === CLEAR_VALUE)) {
        throw new Error(`SelectField: "${CLEAR_VALUE}" is reserved for the clear option.`);
    }

    return (
        <Field
            data-disabled={selectProps.disabled}
            data-invalid={wiring.dataInvalid}
            className={cn(fieldPropsClassName, fieldClassName)}
            {...restFieldProps}
        >
            <FieldLabel htmlFor={wiring.controlId}>{label}</FieldLabel>
            <Select
                {...selectProps}
                onValueChange={(next) => onChange?.(clearable && next === CLEAR_VALUE ? '' : next)}
            >
                <SelectTrigger
                    {...wiring.controlProps}
                    ref={ref}
                    onBlur={onBlur}
                    className={cn(
                        'bg-input-background',
                        inputInvalidClassName,
                        'aria-invalid:focus-visible:border-destructive',
                    )}
                >
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {clearable && (
                        <>
                            <SelectItem value={CLEAR_VALUE} className="text-muted-foreground">
                                {CLEAR_LABEL}
                            </SelectItem>
                            <SelectSeparator />
                        </>
                    )}
                    {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <FieldSlot
                description={description}
                descriptionId={wiring.descriptionId}
                errorMessage={wiring.errorMessage}
                errorId={wiring.errorId}
            />
        </Field>
    );
}

export { SelectField };
